import { c as _c } from "react/compiler-runtime";
import { createContext, PropsWithChildren, useContext } from 'react';
import { TeamRole } from '../../../../../typings/TeamMember.interface';
import { DashboardContext } from '../scenes/DashboardScene/DashboardContext';
import { team_levels_by_role } from '../../../../../domain/roles';
type Props = {
  role: TeamRole;
  children: React.ReactNode;
};
export type TeamPermissionContext = {
  role: TeamRole;
  authorized: boolean;
  shouldCurrentRoleAuthorized: (role: TeamRole) => boolean;
};
const TeamPermissionContext = createContext<TeamPermissionContext>({
  role: 'viewer',
  authorized: true,
  // default to true so components still work when they're used outside of <TeamPermission />
  shouldCurrentRoleAuthorized: () => true
});
export const TeamPermission: React.FC<PropsWithChildren<Props>> = t0 => {
  const $ = _c(9);
  const {
    role,
    children
  } = t0;
  const {
    team_role: current_role
  } = useContext(DashboardContext);
  let t1;
  if ($[0] !== current_role) {
    t1 = allowed_role => current_role ? team_levels_by_role[current_role] >= team_levels_by_role[allowed_role] : false;
    $[0] = current_role;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shouldCurrentRoleAuthorized = t1;
  const authorized = shouldCurrentRoleAuthorized(role);
  let t2;
  if ($[2] !== authorized || $[3] !== role || $[4] !== shouldCurrentRoleAuthorized) {
    t2 = {
      role,
      authorized,
      shouldCurrentRoleAuthorized
    };
    $[2] = authorized;
    $[3] = role;
    $[4] = shouldCurrentRoleAuthorized;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const context = t2;
  let t3;
  if ($[6] !== children || $[7] !== context) {
    t3 = <TeamPermissionContext.Provider value={context}>{children}</TeamPermissionContext.Provider>;
    $[6] = children;
    $[7] = context;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export const withTeamPermission = (Component: any, role: TeamRole) => (props: any) => {
  return <TeamPermission role={role}>
      <Component {...props} />
    </TeamPermission>;
};
export const useTeamPermission = () => {
  return useContext(TeamPermissionContext);
};

/**
 * Helper hook that accepts an optional 'allowed_role' parameter.
 * If not passed, use the nearest allowed role from component tree.
 */
export const useAuthorized = allowed_role => {
  const $ = _c(3);
  const {
    authorized,
    shouldCurrentRoleAuthorized
  } = useContext(TeamPermissionContext);
  if (typeof allowed_role === "undefined") {
    return authorized;
  }
  let t0;
  if ($[0] !== allowed_role || $[1] !== shouldCurrentRoleAuthorized) {
    t0 = shouldCurrentRoleAuthorized(allowed_role);
    $[0] = allowed_role;
    $[1] = shouldCurrentRoleAuthorized;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

/**
 *
 * @param disabled default disabled
 * @param role allowed role
 * @returns boolean
 */
export const usePermissionAwareDisabled = (disabled, role) => {
  return Boolean(!useAuthorized(role) || disabled);
};